import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, CalendarPlus, ChefHat, Music, Truck,
  FileScan, FileText, LogOut, Settings, Wheat, ClipboardList, CalendarDays, Crown
} from "lucide-react";
import AdminFab from "@/components/AdminFab";

const LOGO = "https://customer-assets.emergentagent.com/job_a369e460-91b8-45cf-ab2f-6805bc15e17c/artifacts/riz8gmwr_w13dd3z5_event%20%281%29.png";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
  { to: "/calendario", icon: CalendarDays, label: "Calendario", testid: "nav-calendar" },
  { to: "/eventi", icon: CalendarPlus, label: "Eventi", testid: "nav-events" },
  { to: "/ordini", icon: ClipboardList, label: "Ordini", testid: "nav-orders" },
  { to: "/piatti", icon: ChefHat, label: "Database Piatti", testid: "nav-dishes" },
  { to: "/ingredienti", icon: Wheat, label: "Ingredienti", testid: "nav-ingredients" },
  { to: "/artisti", icon: Music, label: "Artisti", testid: "nav-artists" },
  { to: "/fornitori", icon: Truck, label: "Fornitori", testid: "nav-suppliers" },
  { to: "/fatture", icon: FileScan, label: "OCR Fatture", testid: "nav-invoices" },
  { to: "/piani", icon: Crown, label: "Vedi i piani", testid: "nav-plans" },
  { to: "/impostazioni", icon: Settings, label: "Impostazioni", testid: "nav-settings" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-80 flex-col bg-white border-r border-slate-200 sticky top-0 h-screen">
        <div className="px-4 py-8 border-b border-slate-100 flex justify-center shrink-0">
          <img src={LOGO} alt="Event System 360" className="h-36 object-contain" style={{ filter: "hue-rotate(50deg) saturate(1.2)" }} />
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-6 space-y-1 sidebar-scroll">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-100 shrink-0">
          <div className="flex items-center gap-3 px-3 py-2">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#2563EB] text-white grid place-items-center font-semibold">
                {user?.name?.[0]?.toUpperCase() ?? "?"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0F2741] truncate font-ui">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
            <button onClick={logout} data-testid="btn-logout" className="text-slate-400 hover:text-rose-500 transition-colors" title="Esci">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-40 px-4 py-3 flex items-center justify-between">
        <img src={LOGO} alt="Event System 360" className="h-16 object-contain" style={{ filter: "hue-rotate(50deg) saturate(1.2)" }} />
        <button onClick={logout} className="text-slate-500"><LogOut className="w-5 h-5" /></button>
      </div>

      <main className="flex-1 lg:pl-0 pt-14 lg:pt-0">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 fade-up">
          <Outlet />
        </div>
        {/* Mobile bottom nav */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex justify-around py-2 z-40">
          {navItems.slice(0, 5).map((it) => {
            const Icon = it.icon;
            return (
              <button key={it.to} onClick={() => navigate(it.to)} className="flex flex-col items-center gap-0.5 px-2 py-1 text-slate-500">
                <Icon className="w-5 h-5" />
                <span className="text-[10px]">{it.label.split(" ")[0]}</span>
              </button>
            );
          })}
        </div>
      </main>
      <AdminFab />
    </div>
  );
}
